import { MonitorStatus } from "@hightouch/lib/resource-monitoring/types";
import {
  DiffModeOverride,
  DiffModeOverrideReason,
} from "@hightouch/lib/sync/diff-mode/types";
import { isThisYear, isToday, isYesterday } from "date-fns";
import { isEnum } from "src/types/utils";
import {
  resyncReasonLabel,
  SyncRunStatus,
  SyncRunStatusOrUnknownStatus,
  UnknownSyncRunStatus,
} from "src/utils/syncs";
import * as time from "src/utils/time";

export const isMonitorStatus = isEnum(MonitorStatus);

const getCreatedAtPrefix = (createdAt: string): string | null => {
  const date = new Date(createdAt);
  if (isToday(date)) {
    return "Today";
  } else if (isYesterday(date)) {
    return "Yesterday";
  } else {
    const format = isThisYear(date) ? "MMM d" : "MMM d, yyyy";
    return time.formatDatetime(createdAt, format);
  }
};

export const formatRunCreatedAt = (createdAt: string): string => {
  const prefix = getCreatedAtPrefix(createdAt);
  const suffix = time.formatDatetime(createdAt, "p");

  // Date isn't parsable, just return the original string
  if (!prefix || !suffix) return createdAt;

  return `${prefix} · ${suffix}`;
};

export const syncRunStatusDurationText = (
  status: SyncRunStatusOrUnknownStatus,
  createdAt: string,
  finishedAt: string | null,
): string | null => {
  const duration = time.formatDuration(
    {
      start: createdAt,
      end: (finishedAt ? new Date(finishedAt) : new Date()).toISOString(),
    },
    {
      units: "short",
    },
  );

  if (!duration) return null;

  switch (status) {
    case SyncRunStatus.SUCCESS:
    case SyncRunStatus.WARNING:
      return `in ${duration}`;
    case SyncRunStatus.FAILED:
    case SyncRunStatus.CANCELED:
    case SyncRunStatus.INTERRUPTED:
      return `after ${duration}`;
    case SyncRunStatus.ENQUEUED:
    case SyncRunStatus.QUERYING:
    case SyncRunStatus.PROCESSING:
    case SyncRunStatus.REPORTING:
    case SyncRunStatus.ACTIVE:
    case SyncRunStatus.IN_PROGRESS:
    case SyncRunStatus.ABORTED:
    case SyncRunStatus.PENDING:
    case SyncRunStatus.PREPARING:
    case SyncRunStatus.UNPROCESSABLE:
    case SyncRunStatus.DISABLED:
    case SyncRunStatus.INCOMPLETE:
    case UnknownSyncRunStatus:
      return `${duration} elapsed`;
    case SyncRunStatus.NEVER_RUN:
    default:
      return null;
  }
};

export const isSyncRunning = (status: SyncRunStatusOrUnknownStatus) => {
  switch (status) {
    case SyncRunStatus.ENQUEUED:
    case SyncRunStatus.QUERYING:
    case SyncRunStatus.PROCESSING:
    case SyncRunStatus.REPORTING:
    case SyncRunStatus.ACTIVE:
    case SyncRunStatus.IN_PROGRESS:
    case SyncRunStatus.PENDING:
    case SyncRunStatus.NEVER_RUN:
    case SyncRunStatus.PREPARING:
      return true;
    case SyncRunStatus.SUCCESS:
    case SyncRunStatus.WARNING:
    case SyncRunStatus.FAILED:
    case SyncRunStatus.CANCELED:
    case SyncRunStatus.INTERRUPTED:
    case SyncRunStatus.ABORTED:
    case SyncRunStatus.DISABLED:
    case SyncRunStatus.UNPROCESSABLE:
    case SyncRunStatus.INCOMPLETE:
    case UnknownSyncRunStatus:
    default:
      return false;
  }
};

// A scheduled refresh keeps CDC intact, so the backend persists full_resync +
// resync_reason but leaves diff_mode_override null (unlike a manual full resync
// or reset). Detect it via resync_reason first so the run tables can label it,
// then fall back to the diff_mode_override for manual resets/resyncs.
export const getSyncRunResyncLabel = ({
  diffModeOverride,
  resyncReason,
}: {
  diffModeOverride: string | null | undefined;
  resyncReason: string | null | undefined;
}): string | null => {
  if (resyncReason === DiffModeOverrideReason.ResyncInterval) {
    return resyncReasonLabel(resyncReason);
  }
  if (!isEnum(DiffModeOverride)(diffModeOverride)) {
    return null;
  }
  switch (diffModeOverride) {
    case DiffModeOverride.ResetCdc:
      return "Reset CDC";
    case DiffModeOverride.FullResync:
    case DiffModeOverride.ClearAndFill:
      return "Full resync";
    default:
      return null;
  }
};
